import React, { MouseEventHandler, useState } from 'react';
import styled from 'styled-components';
import { IPathNode } from './interfaces';
import { formatNumberWithComma } from '../../../utils/formatText';
import {
  EventValue,
  LabelValuePairRow,
  NodeValueDisplay,
  TitleRow,
} from './TooltipUI';
import { getNodeStats, isMergedNode } from './utils';
import { isNil } from 'lodash';
import InformationalTooltip from '../../InformationalTooltip';
import { getScrollBarStyles } from '../../../config/styles';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 4px;
`;

const NodesWrapper = styled.div`
  flex: 1;
  max-height: 120px;
  overflow-y: auto;
  ${getScrollBarStyles(2)};
`;

const TooltipContainer = styled.div`
  margin-top: -2px;
  vertical-align: middle;
  display: inline-flex;
  align-items: center;
`;

const Footer = styled.div`
  flex: 0 0 auto;
  padding-right: 4px;
  padding-bottom: 4px;
  padding-left: 4px;
`;

const ToggleDetailsButton = styled.button<{ $bgColor: string }>`
  padding: 2px 8px;
  border: none;
  border-radius: 4px;
  background-color: ${({ $bgColor }) => $bgColor};
  font: inherit;
  font-size: 10px;
  font-weight: 500;
  outline: none;
  line-height: normal;
  color: #fff;
`;

interface IProps {
  node: IPathNode;
  color: string;
  isBase: boolean;
  isObserveMode: boolean;
}

export default ({ node, isBase, color, isObserveMode }: IProps) => {
  const { name, groupName, mergedNodes } = node;
  const [showMergedEvents, setShowMergedEvents] = useState(false);

  const toggleDetailsButtonHandler: MouseEventHandler<HTMLElement> = (
    event,
  ) => {
    setShowMergedEvents((prevState) => !prevState);
    event.stopPropagation();
  };

  const displayText = showMergedEvents ? `${name} - Details` : name;
  const showDetailsButton = isMergedNode(node) && isObserveMode;

  const { percent, counts, tooltip } = getNodeStats(node);

  return (
    <Container>
      <TitleRow $color={color}>
        {!isBase && <>{groupName} - </>}
        {displayText}
        {!showMergedEvents && (
          <>
            :{' '}
            <NodeValueDisplay>
              {percent} ({counts})
            </NodeValueDisplay>
            {!isNil(tooltip) && (
              <>
                {' '}
                <TooltipContainer>
                  <InformationalTooltip
                    tooltipOption={{
                      content: tooltip,
                    }}
                    size={14}
                  />
                </TooltipContainer>
              </>
            )}
          </>
        )}
      </TitleRow>
      {showDetailsButton && (
        <>
          {showMergedEvents && mergedNodes.length > 0 && (
            <NodesWrapper>
              {mergedNodes.map((node) => (
                <LabelValuePairRow key={node.key}>
                  <span>{node.name}</span>
                  <EventValue>
                    {formatNumberWithComma(node.incomingCount)}
                  </EventValue>
                </LabelValuePairRow>
              ))}
            </NodesWrapper>
          )}
          <Footer>
            <ToggleDetailsButton
              type="button"
              onClick={toggleDetailsButtonHandler}
              $bgColor={color}
            >
              {showMergedEvents ? 'Hide Details' : 'Show Details'}
            </ToggleDetailsButton>
          </Footer>
        </>
      )}
    </Container>
  );
};
